import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Gauge, Loader2, RefreshCcw, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
    fragile: 'bg-red-50 text-red-700 border-red-200',
    structuration: 'bg-amber-50 text-amber-800 border-amber-200',
    prometteur: 'bg-blue-50 text-blue-700 border-blue-200',
    solide: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const barColor = (pct) => pct >= 67 ? 'bg-emerald-500' : pct >= 34 ? 'bg-amber-500' : 'bg-red-400';
const fmtDate = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

export function ProjectScoreCard({ projectId, workspaceId }) {
    const { session } = useAuth();
    const headers = session ? { Authorization: `Bearer ${session.access_token}` } : null;
    const [history, setHistory] = useState(null);
    const [dims, setDims] = useState([]);
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [busy, setBusy] = useState(false);

    const reload = useCallback(() => {
        if (!headers || !workspaceId) return;
        axios.get(`${API}/projects/${projectId}/score/history?workspace_id=${workspaceId}`, { headers })
            .then(r => setHistory(r.data)).catch(() => setHistory({ items: [], latest: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, workspaceId, session]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => {
        axios.get(`${API}/project-score/questionnaire`).then(r => setDims(r.data.dimensions || [])).catch(() => { });
    }, []);

    const startQuiz = () => { setAnswers({}); setCurrent(0); setOpen(true); };

    const answer = (key, idx) => {
        const next = { ...answers, [key]: idx };
        setAnswers(next);
        if (current < dims.length - 1) {
            setTimeout(() => setCurrent(c => Math.min(c + 1, dims.length - 1)), 200);
        }
    };

    const allAnswered = dims.length > 0 && dims.every(d => answers[d.key] !== undefined);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/projects/${projectId}/score`,
                { workspace_id: workspaceId, answers }, { headers });
            toast.success(`Score enregistré : ${r.data.total}/100 — ${r.data.level.label}`);
            setOpen(false);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    if (!history) return null;
    const latest = history.latest;
    const prev = history.items?.[1];
    const delta = latest && prev ? latest.total - prev.total : null;
    const dim = dims[current];

    return (
        <div className="card-soft p-6" data-testid="project-score-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Gauge size={17} className="text-primary" />
                    <h3 className="font-heading text-lg">Project Score™</h3>
                </div>
                <Button size="sm" variant="outline" onClick={startQuiz} disabled={!dims.length}
                    className="border-primary/40 text-primary hover:bg-primary-light"
                    data-testid="project-score-evaluate-btn">
                    <RefreshCcw size={13} className="mr-1.5" /> {latest ? 'Réévaluer' : 'Évaluer ce projet'}
                </Button>
            </div>

            {!latest ? (
                <p className="text-sm text-foreground/55 mt-4" data-testid="project-score-empty">
                    Ce projet n'a pas encore été évalué. Répondez aux 8 dimensions (3 min) pour obtenir
                    son score de maturité /100 et suivre sa progression.
                </p>
            ) : (
                <div className="mt-4 flex items-center gap-6 flex-wrap">
                    <div className="flex items-baseline gap-2">
                        <span className="font-heading text-4xl" data-testid="project-score-total">{latest.total}</span>
                        <span className="text-sm text-muted-foreground">/ 100</span>
                        {delta !== null && delta !== 0 && (
                            <span className={`text-xs font-mono ${delta > 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="project-score-delta">
                                {delta > 0 ? '+' : ''}{delta}
                            </span>
                        )}
                    </div>
                    <span className={`px-3 py-1 rounded-full border text-xs font-medium ${LEVEL_COLORS[latest.level?.key] || ''}`}
                        data-testid="project-score-level">
                        {latest.level?.label}
                    </span>
                    <span className="text-xs text-muted-foreground">Évalué le {fmtDate(latest.created_at)}</span>
                </div>
            )}

            {latest && (
                <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2">
                    {latest.per_dimension.map(d => (
                        <div key={d.key} className="flex items-center gap-2.5">
                            <span className="text-xs w-40 shrink-0 text-foreground/70 truncate">{d.label}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                                <div className={`h-full ${barColor(d.percent)} rounded-full`} style={{ width: `${d.percent}%` }} />
                            </div>
                            <span className="text-[10px] font-mono text-muted-foreground w-10 text-right">{d.points}/{d.weight}</span>
                        </div>
                    ))}
                </div>
            )}

            {history.items?.length > 1 && (
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 flex-wrap" data-testid="project-score-history">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">Historique</span>
                    {history.items.slice(0, 8).map(h => (
                        <span key={h.id} className="px-2 py-0.5 rounded-md bg-surface-muted text-xs font-mono text-foreground/75"
                            title={fmtDate(h.created_at)}>
                            {h.total}
                        </span>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl" data-testid="project-score-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Évaluer ce projet — Project Score™</DialogTitle>
                        <DialogDescription>8 dimensions, environ 3 minutes. Le score est historisé sur ce projet.</DialogDescription>
                    </DialogHeader>
                    {dim && (
                        <div>
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.15em] text-foreground/55 mb-2">
                                <span>Dimension {current + 1} / {dims.length}</span>
                                <span>{dim.weight} pts</span>
                            </div>
                            <div className="h-1 rounded-full bg-surface-muted overflow-hidden mb-4">
                                <div className="h-full bg-primary transition-all duration-300"
                                    style={{ width: `${Math.round(((current + (answers[dim.key] !== undefined ? 1 : 0)) / dims.length) * 100)}%` }} />
                            </div>
                            <div className="eyebrow mb-1 text-secondary">{dim.label}</div>
                            <p className="font-medium mb-4">{dim.question}</p>
                            <div className="space-y-2">
                                {dim.options.map((opt, idx) => (
                                    <button type="button" key={idx} onClick={() => answer(dim.key, idx)}
                                        className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-all
                                            ${answers[dim.key] === idx ? 'border-primary bg-primary-light/60' : 'border-border hover:border-primary/40'}`}
                                        data-testid={`pscore-option-${dim.key}-${idx}`}>
                                        <span className="inline-flex items-center gap-2.5">
                                            <span className={`h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${answers[dim.key] === idx ? 'border-primary bg-primary text-white' : 'border-border'}`}>
                                                {answers[dim.key] === idx && <Check size={10} />}
                                            </span>
                                            {opt}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center justify-between mt-5">
                                <button type="button" onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
                                    className="text-xs text-foreground/55 hover:text-foreground disabled:opacity-30 inline-flex items-center gap-1">
                                    <ArrowLeft size={12} /> Précédente
                                </button>
                                {current === dims.length - 1 ? (
                                    <Button onClick={submit} disabled={!allAnswered || busy}
                                        className="bg-primary hover:bg-primary-hover" data-testid="pscore-submit-btn">
                                        {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                                        Enregistrer le score
                                    </Button>
                                ) : (
                                    <button type="button" onClick={() => setCurrent(c => Math.min(dims.length - 1, c + 1))}
                                        disabled={answers[dim.key] === undefined}
                                        className="text-xs text-primary hover:underline disabled:opacity-30 inline-flex items-center gap-1"
                                        data-testid="pscore-next-btn">
                                        Suivante <ArrowRight size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
